import { BaseService } from './BaseService';
import { apiClient as httpClient } from './api';
import {
  InvoiceTemplateConfig,
  InvoiceTemplateConfigCreate,
  InvoiceTemplateConfigUpdate,
  PaymentConfiguration,
  PaymentConfigurationCreate,
  PaymentConfigurationUpdate,
  DocumentNumberSequence,
  DocumentNumberSequenceCreate,
  DocumentNumberSequenceUpdate,
  ConfigurationFilters
} from '../types/configuration';

class InvoiceTemplateConfigService extends BaseService<
  InvoiceTemplateConfig,
  InvoiceTemplateConfigCreate,
  InvoiceTemplateConfigUpdate,
  ConfigurationFilters
> {
  protected baseUrl = '/admin/invoice-template-configs';

  // Backend now returns standard 'items' field - no override needed
  // Removed custom getList() - using BaseService implementation

  async setAsDefault(id: string): Promise<InvoiceTemplateConfig> {
    const response = await this.apiClient.post(`${this.baseUrl}/${id}/set-default`);
    return response.data;
  }

  async getByDocumentType(documentType: string): Promise<InvoiceTemplateConfig[]> {
    const response = await this.apiClient.get(`${this.baseUrl}?document_type=${documentType}`);
    return response.data.items || [];
  }

  async previewTemplate(id: string): Promise<Blob> {
    const response = await this.apiClient.get(`${this.baseUrl}/${id}/preview`, {
      responseType: 'blob'
    });
    return response.data;
  }

  private get apiClient() {
    return httpClient;
  }
}

class PaymentConfigurationService extends BaseService<
  PaymentConfiguration,
  PaymentConfigurationCreate,
  PaymentConfigurationUpdate,
  ConfigurationFilters
> {
  protected baseUrl = '/admin/payment-configurations';

  async getActive(): Promise<PaymentConfiguration | null> {
    const response = await this.apiClient.get(`${this.baseUrl}/active`);
    return response.data;
  }

  async testConnection(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.apiClient.post(`${this.baseUrl}/${id}/test`);
    return response.data;
  }

  private get apiClient() {
    return httpClient;
  }
}

class DocumentNumberSequenceService extends BaseService<
  DocumentNumberSequence,
  DocumentNumberSequenceCreate,
  DocumentNumberSequenceUpdate,
  ConfigurationFilters
> {
  protected baseUrl = '/admin/document-sequences';

  // The backend addresses sequences by document type (PATCH /{document_type}); current_number can only change via reset.
  async updateByType(documentType: string, data: DocumentNumberSequenceUpdate): Promise<DocumentNumberSequence> {
    const { current_number: _ignored, ...payload } = data;
    const response = await this.apiClient.patch(`${this.baseUrl}/${documentType}`, payload);
    return response.data;
  }

  async resetSequence(documentType: string, newStartNumber: number, reason: string): Promise<DocumentNumberSequence> {
    const response = await this.apiClient.post(`${this.baseUrl}/${documentType}/reset`, {
      new_start_number: newStartNumber,
      reason
    });
    return response.data;
  }

  async getByDocumentType(documentType: string): Promise<DocumentNumberSequence | null> {
    const response = await this.apiClient.get(`${this.baseUrl}/by-type/${documentType}`);
    return response.data;
  }

  async getCurrentNumber(documentType: string): Promise<{ current_number: number; next_number: string }> {
    const response = await this.apiClient.get(`${this.baseUrl}/current/${documentType}`);
    return response.data;
  }

  private get apiClient() {
    return httpClient;
  }
}

export const invoiceTemplateConfigService = new InvoiceTemplateConfigService();
export const paymentConfigurationService = new PaymentConfigurationService();
export const documentNumberSequenceService = new DocumentNumberSequenceService();

export default {
  invoiceTemplateConfig: invoiceTemplateConfigService,
  paymentConfiguration: paymentConfigurationService,
  documentNumberSequence: documentNumberSequenceService
};